import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OTHER_NEIGHBORHOOD } from '../utils/saudiNeighborhoods';
import './SearchableNeighborhoodSelect.css';

/**
 * Searchable neighborhood picker (filter-as-you-type).
 * value: neighborhood string or OTHER_NEIGHBORHOOD
 */
export default function SearchableNeighborhoodSelect({
  language = 'ar',
  city = '',
  options = [],
  value = '',
  onChange,
  disabled = false,
  required = false,
  id,
}) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    if (value === OTHER_NEIGHBORHOOD) return t('حي آخر (اكتب الاسم)', 'Other (type name)');
    const hit = options.find((o) => o.value === value);
    if (hit) return isAr ? hit.labelAr : hit.labelEn;
    return value;
  }, [value, options, isAr]);

  useEffect(() => {
    if (!open) setQuery(selectedLabel);
  }, [selectedLabel, open, city]);

  useEffect(() => {
    // Reset text when city changes and value cleared
    if (!value) setQuery('');
  }, [city, value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const list = options || [];
    if (!q || (selectedLabel && q === String(selectedLabel).toLowerCase())) return list;
    return list.filter((o) => {
      const ar = String(o.labelAr || o.value || '').toLowerCase();
      const en = String(o.labelEn || '').toLowerCase();
      return ar.includes(q) || en.includes(q) || String(o.value).toLowerCase().includes(q);
    });
  }, [options, query, selectedLabel]);

  const pick = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
    if (nextValue === OTHER_NEIGHBORHOOD) {
      setQuery(t('حي آخر (اكتب الاسم)', 'Other (type name)'));
    } else {
      const hit = options.find((o) => o.value === nextValue);
      setQuery(hit ? (isAr ? hit.labelAr : hit.labelEn) : nextValue);
    }
  };

  const placeholder = !city
    ? t('اختر المدينة أولاً', 'Select city first')
    : t('ابحث عن الحي…', 'Search neighborhood…');

  return (
    <div
      className={`sns${disabled ? ' sns--disabled' : ''}${city && !disabled ? ' sns--ready' : ''}`}
      ref={rootRef}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <input
        id={id}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-required={required || undefined}
        disabled={disabled || !city}
        value={open ? query : selectedLabel || query}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          if (!disabled && city) {
            setOpen(true);
            setQuery('');
          }
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange?.('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            setQuery(selectedLabel);
            inputRef.current?.blur();
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered.length === 1) pick(filtered[0].value);
            else if (filtered.length > 0 && query.trim()) pick(filtered[0].value);
          }
        }}
      />
      {open && city && !disabled && (
        <ul className="sns__list" role="listbox">
          {filtered.length === 0 && (
            <li className="sns__empty">{t('لا نتائج', 'No matches')}</li>
          )}
          {filtered.map((opt) => {
            const label = isAr ? opt.labelAr : opt.labelEn;
            const active = value === opt.value;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`sns__option${active ? ' sns__option--active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(opt.value)}
                >
                  {label}
                </button>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              role="option"
              aria-selected={value === OTHER_NEIGHBORHOOD}
              className={`sns__option sns__option--other${value === OTHER_NEIGHBORHOOD ? ' sns__option--active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(OTHER_NEIGHBORHOOD)}
            >
              {t('حي آخر (اكتب الاسم)', 'Other (type name)')}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
